import { IBatch } from '@core/models/batch.model';
import { toast } from 'react-toastify';
import { IErrorModel, ISuccessResponse, IPaginatedResponse } from '@core/models/generic.model';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import batchService from 'services/api/batch.service';

// -------------------------------------------------------------------------

interface IBatchesResponse extends IPaginatedResponse {
    content: IBatch[];
}

interface IBatchState {
    loading: boolean;
    createLoading: boolean;
    detailLoading: boolean;
    error: IErrorModel | undefined;
    batches: IBatchesResponse | null;
    batchDetail: IBatch | null;
}

const initialState: IBatchState = {
    loading: false,
    createLoading: false,
    detailLoading: false,
    error: {} as IErrorModel,
    batches: null,
    batchDetail: null,
};

// -------------------------------------------------------------------------

export const getBatchesThunk = createAsyncThunk<
    IBatchesResponse,
    { pageSize: number; pageNumber: number },
    { rejectValue: IErrorModel }
>(`/batch/getBatches`, async ({ pageSize, pageNumber }, { rejectWithValue }: any) => {
    try {
        const response: any = await batchService.getBatches({ pageSize, pageNumber });
        return response.data.Batches;
    } catch (err: any) {
        toast.error(err.errorMessage || 'Failed to fetch batches', { autoClose: 5000 });
        return rejectWithValue(err);
    }
});

export const getBatchByIdThunk = createAsyncThunk<IBatch, number, { rejectValue: IErrorModel }>(
    `/batch/getBatchById`,
    async (id, { rejectWithValue }: any) => {
        try {
            const response: any = await batchService.getBatchById(id);
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to load batch detail', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

export const createBatchThunk = createAsyncThunk<ISuccessResponse, any, { rejectValue: IErrorModel }>(
    `/batch/createBatch`,
    async (body, { rejectWithValue }: any) => {
        try {
            const response: any = await batchService.addBatch(body);
            toast.success(response.data.message || 'Batch created successfully', { autoClose: 5000 });
            return response.data;
        } catch (err: any) {
            toast.error(err.errorMessage || 'Failed to create batch', { autoClose: 5000 });
            return rejectWithValue(err);
        }
    }
);

// -------------------------------------------------------------------------

const batchSlice = createSlice({
    name: `batch`,
    initialState,
    reducers: {
        clearBatchDetail: (state) => {
            state.batchDetail = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getBatchesThunk.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(getBatchesThunk.fulfilled, (state, action) => {
            state.loading = false;
            state.batches = action.payload;
        });
        builder.addCase(getBatchesThunk.rejected, (state, action) => {
            state.loading = false;
            state.batches = null;
            state.error = action.payload;
        });

        builder.addCase(getBatchByIdThunk.pending, (state) => {
            state.detailLoading = true;
        });
        builder.addCase(getBatchByIdThunk.fulfilled, (state, action) => {
            state.detailLoading = false;
            state.batchDetail = action.payload;
        });
        builder.addCase(getBatchByIdThunk.rejected, (state, action) => {
            state.detailLoading = false;
            state.batchDetail = null;
            state.error = action.payload;
        });

        builder.addCase(createBatchThunk.pending, (state) => {
            state.createLoading = true;
        });
        builder.addCase(createBatchThunk.fulfilled, (state) => {
            state.createLoading = false;
        });
        builder.addCase(createBatchThunk.rejected, (state, action) => {
            state.createLoading = false;
            state.error = action.payload;
        });
    },
});

// -------------------------------------------------------------------------

export const { clearBatchDetail } = batchSlice.actions;
export default batchSlice;
export const batchReducer = batchSlice.reducer;
